/**
 * Lead Inbox + triage — ADMIN / STAFF track.
 *
 * CHAT-FIRST CORRECTION BATCH (2026-05-18):
 *  · Layout is now side-by-side master–detail on desktop (list left,
 *    triage detail right pane), stacked on mobile. The old bottom-panel
 *    triage is removed.
 *  · A chat surface is the PRIMARY way to triage a lead (ChatSurface,
 *    lib/localChat — same chat-first model as the Project Conversation).
 *    The existing triage form is retained as the manual FALLBACK layer
 *    beneath chat. AI→data: chatting proposes the same field writes the
 *    form would take; "Apply" maps the proposal onto the form state.
 *
 * THE FRONT DOOR. Steps 2–7 of the 14-step Client Journey Spine. All
 * pricing / state-machine / convert logic stays the PURE leads.ts
 * (unit-tested) — this screen only renders + dispatches. The 8 seed
 * leads (Test L1A–L3B) load from leadDemo.ts, intact.
 *
 * Client never reaches this route (RequireAuth roles gate, App.tsx).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { setProjectAgreedPrice } from '../lib/recordPaymentStore';
import { visibilityFor } from '../lib/roles';
import ChatSurface from '../components/ChatSurface';
import SubmissionDetails from '../components/SubmissionDetails';
import SetPrice from '../components/SetPrice';
import RecordPaymentFlow from '../components/RecordPaymentFlow';
import IntakeLinkSendBar from '../components/IntakeLinkSendBar';
import { ClientPicker } from '../components/ClientPicker';
import { Box, Btn3, Card, EmptyNote, Pill, Tag } from '../components/ui';
import { BookConsultPanel } from '../features/consulting/components/BookConsultPanel';
import { subscribeConsultingFlag } from '../features/consulting/lib/flags';
import {
  isLeadComplete,
  leadChecklist,
  completionMessage,
  convertGate,
} from '../lib/leadQuote';
import { simulateLeadsEnabled } from '../lib/devFlags';
import {
  ALL_LEAD_STATES,
  LEAD_SOURCE_LABEL,
  LEAD_STATE_LABEL,
  leadAgeDays,
  needsIdentityCapture,
  nextStates,
  recordConsultBooked,
  recordDiscoveryDone,
  resolveDetail,
  transitionLead,
  type Lead,
  type LeadInboundDraft,
  type LeadState,
} from '../lib/leads';
import {
  listenLeads,
  trashLeadDoc,
  updateLead,
  updateLeadAdmin,
} from '../lib/leadStore';
import { BLANK_MANUAL, createInboundLead } from '../lib/leadCreate';
import { canManageTrash } from '../lib/trash';
import {
  appendLeadRef,
  clientRowsFromProjects,
  convertLeadPersist,
  findOpenSentQuoteIdForLead,
  listenClients,
  listenPartners,
  listenProjects,
  matchesClientRow,
  matchLeadToClient,
  mergeClientRows,
  type ClientMatch,
  type ClientRow,
  type ConvertClientTarget,
  type LiveClient,
  type LivePartner,
  type LiveProject,
} from '../lib/projectStore';
import { findOpenSentQuoteForLead } from '../lib/quoteStore';
import {
  bestAutoLink,
  matchContacts,
  type ContactLite,
  type MatchResult,
} from '../lib/contactMatch';
import { inr } from '../lib/trackDemo';
import { useIsMobile } from '../lib/useIsMobile';
import type { Tier } from '../lib/engagementTemplate';

/** Wall-clock ISO — the ONLY impure bit; the pure layer takes it as arg. */
const nowISO = () => new Date().toISOString();

export default function Leads() {
  const { role, user } = useAuth();
  const { toast } = useToast();
  const vis = visibilityFor(role);
  // BUG 1 (2026-05-22) — on a phone the Leads screen shows ONE in-flow
  // view at a time: the list, or the selected lead's triage detail (with a
  // back-to-list control). No floating / sticky triage pane pinned over the
  // list. The breakpoint is the SAME 860px the app shell uses (useIsMobile).
  const isMobile = useIsMobile();
  // LIVE Firestore-backed inbox (task #888). No more in-memory seed —
  // every create/transition/quote/convert persists to `leads/` and
  // streams back here, so a refresh keeps real work.
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<LeadState | 'all'>('all');
  const [seq, setSeq] = useState(0);
  // BULK SELECT (2026-05-22) — the owner has a large backlog of junk leads
  // (parked / rejected) to drain. `selectMode` turns the inbox into a
  // multi-select surface; `picked` holds the chosen lead ids. Capability-
  // gated (trash.manage) — staff park/reject one-by-one is unchanged.
  const mayTrash = canManageTrash(role);
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // BUG 3 (2026-05-22) — per-row "move to Trash" affordance on the lead
  // cards. The action existed but was buried in the detail's fallback box.
  // `confirmTrashId` holds the id of the card whose inline confirm is open.
  const [confirmTrashId, setConfirmTrashId] = useState<string | null>(null);
  // IA FIX (spec Q7) — the existing-client directory for lead conversion.
  // Built by merging the live project fold with the `clients/` masters —
  // the SAME `mergeClientRows` the Clients screen + ClientPicker use — so a
  // standalone client (0 projects) is pickable AND every row shows the
  // CANONICAL master name (not a drifted per-project cache).
  // The live `projects/` stream. Doubles as (a) the existing-client directory
  // source (folded via clientRowsFromProjects) AND (b) the project list the
  // inline Record-payment modal needs — so payment is recorded on the SAME
  // lead record after Convert, no jump to the Money screen (Module 1).
  const [clientProjectsRaw, setClientProjectsRaw] = useState<LiveProject[]>([]);
  const [clientMasters, setClientMasters] = useState<LiveClient[]>([]);
  const knownClients: ClientRow[] = useMemo(
    () =>
      mergeClientRows(
        clientRowsFromProjects(clientProjectsRaw, true),
        clientMasters,
        true,
      ),
    [clientProjectsRaw, clientMasters],
  );
  // PARTNER SPRINT 2026-05-21 — the partner directory for the optional
  // channel-partner picker on convert (spec §3 — a project may have a
  // partner, or none). Un-archived partners only.
  const [knownPartners, setKnownPartners] = useState<LivePartner[]>([]);

  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'You';

  // Realtime stream of every front-door lead. Newest first.
  useEffect(() => {
    const unsub = listenLeads(
      (live) => {
        setLeads(live);
        setSeq((s) => Math.max(s, live.length));
      },
      () => toast('Could not load leads (check connection)'),
    );
    return unsub;
  }, [toast]);

  // Live client directory for the convert → existing-client picker.
  // Two streams — the projects (folded into client rows) AND the `clients/`
  // masters — merged so the directory has canonical names + standalone
  // clients. Mirrors the ClientPicker component's data wiring.
  useEffect(() => {
    const unsub = listenProjects(
      (projects) => setClientProjectsRaw(projects),
      () => {
        /* the picker just shows no existing clients if this fails */
      },
    );
    return unsub;
  }, []);
  useEffect(() => {
    const unsub = listenClients(
      (masters) => setClientMasters(masters),
      () => {
        /* a standalone client just won't appear if this stream fails */
      },
    );
    return unsub;
  }, []);

  // Live partner directory for the optional convert → partner picker.
  useEffect(() => {
    const unsub = listenPartners(
      (rows) => setKnownPartners(rows.filter((p) => !p.archived)),
      () => {
        /* the picker just shows no partners if this fails */
      },
    );
    return unsub;
  }, []);

  // Master–detail: the pure resolver decides the detail subject (tested).
  const selected = resolveDetail(leads, selectedId);

  const shown = useMemo(
    () => (filter === 'all' ? leads : leads.filter((l) => l.state === filter)),
    [leads, filter],
  );

  /* Persist a pure-mutated lead. The pure mutators (transitionLead /
     quoteLead / convert) compute the new Lead; this writes it through the
     rules-whitelisted leadStore path and the listener echoes it back. */
  async function persist(next: Lead, what: string) {
    try {
      await updateLead(next, actorName, what);
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`);
    }
  }

  function upsert(next: Lead) {
    // Optimistic local echo so the UI is instant; the listener reconciles.
    setLeads((cur) => cur.map((l) => (l.id === next.id ? next : l)));
    void persist(next, 'Lead updated');
  }

  async function ingest(draft: LeadInboundDraft) {
    const n = seq + 1;
    setSeq(n);
    // The ONE shared create path (lib/leadCreate — parity GAP 1): contact-
    // spine match (AD-039) → pure makeLead → createLead → reverse back-ref.
    // The List/Board lens toolbars call the SAME function.
    try {
      const id = await createInboundLead(
        draft,
        clientMasters as unknown as ContactLite[],
        n,
        actorName,
        nowISO(),
      );
      setSelectedId(id);
    } catch (e) {
      toast(`Could not create lead: ${(e as Error).message}`);
    }
  }

  /* ── Bulk select (junk-lead drain) ── */
  function togglePick(id: string) {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Move every picked lead to the recoverable Trash. */
  async function bulkTrash() {
    if (bulkBusy || picked.size === 0) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    for (const id of picked) {
      const lead = leads.find((l) => l.id === id);
      if (!lead) continue;
      try {
        await trashLeadDoc(lead, actorName, nowISO());
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setPicked(new Set());
    setSelectMode(false);
    toast(
      `Moved ${ok} lead${ok === 1 ? '' : 's'} to Trash` +
        (fail ? ` · ${fail} failed` : '') +
        '. Recover them from the Trash screen.',
    );
  }

  /** Move ONE lead to the recoverable Trash (per-row action). */
  async function trashOne(lead: Lead) {
    try {
      await trashLeadDoc(lead, actorName, nowISO());
      toast(`Lead moved to Trash — recover it from the Trash screen.`);
      if (selectedId === lead.id) setSelectedId(null);
    } catch (e) {
      toast(`Could not move to Trash: ${(e as Error).message}`);
    }
  }

  // BUG 1 — which pane to render. On desktop BOTH show (master–detail
  // grid). On mobile exactly ONE shows: the detail when a lead is picked,
  // otherwise the list. `position:sticky` is stripped from the detail at
  // mobile width by CSS, so nothing floats over the list regardless.
  const showList = !isMobile || !selectedId;
  const showDetail = !isMobile || !!selectedId;

  return (
    <div className="lead-md">
      {/* ───── master : list ───── */}
      {showList && (
      <div className="lead-list">
        <Box
          title="Lead Inbox"
          note={`${role === 'admin' ? 'All leads' : 'Leads'} · the front door — 3 entry paths feed this`}
        >
          {/* STICKY LIST 2026-05-21 — the actions + filter pills stay
              pinned (.lead-filters); only the rows below scroll. */}
          <div className="lead-filters">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <Btn3 intent="ink" size="sm" onClick={() => ingest(BLANK_MANUAL())}>
                + New Lead
              </Btn3>
              {/* DEMO-ONLY (streamline 1e) — the Simulate buttons are hidden
                  on the prod screen unless `?simLeads=1` is literally in the
                  URL for THIS page-load (URL-explicit only, never persisted —
                  owner incident 2026-07-03, see devFlags). */}
              {simulateLeadsEnabled() && (
                <>
                  <Btn3 intent="ghost" size="sm" onClick={() => ingest(SIM_WEBSITE())}>
                    Simulate website
                  </Btn3>
                  <Btn3 intent="ghost" size="sm" onClick={() => ingest(SIM_FREE_TOOL())}>
                    Simulate free-tool
                  </Btn3>
                  {/* MODULE 1 — "Lead arrives via WhatsApp". Uses the existing
                      inbound/simulate path (WhatsApp OUTBOUND stays OFF). The
                      contact then matches an existing client by phone in the
                      inbox + record (e.g. Sumit C00407 — no duplicate). */}
                  <Btn3 intent="ghost" size="sm" onClick={() => ingest(SIM_WHATSAPP())}>
                    Simulate WhatsApp
                  </Btn3>
                </>
              )}
              {/* BULK SELECT — junk-lead drain. Capability-gated. */}
              {mayTrash && (
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectMode((m) => !m);
                    setPicked(new Set());
                    setConfirmTrashId(null);
                  }}
                  title="Select multiple leads to move to Trash together"
                >
                  {selectMode ? 'Cancel' : '🗑 Select to delete'}
                </Btn3>
              )}
            </div>

            {/* Bulk-action bar — visible only in select mode. */}
            {selectMode && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  marginBottom: 10,
                  padding: '8px 10px',
                  border: '1px solid var(--rule-strong)',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--accent-wash)',
                }}
              >
                <label
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      shown.length > 0 && picked.size === shown.length
                    }
                    onChange={() =>
                      setPicked((s) =>
                        s.size === shown.length
                          ? new Set()
                          : new Set(shown.map((l) => l.id)),
                      )
                    }
                  />
                  Select all in view ({shown.length})
                </label>
                <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
                  {picked.size} selected
                </span>
                <Btn3
                  intent="ink"
                  size="sm"
                  onClick={bulkTrash}
                  disabled={bulkBusy || picked.size === 0}
                >
                  {bulkBusy
                    ? 'Moving…'
                    : `Move ${picked.size || ''} to Trash`}
                </Btn3>
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Pill on={filter === 'all'} onClick={() => setFilter('all')}>
                All ({leads.length})
              </Pill>
              {ALL_LEAD_STATES.map((s) => {
                const c = leads.filter((l) => l.state === s).length;
                if (c === 0 && filter !== s) return null;
                return (
                  <Pill key={s} on={filter === s} onClick={() => setFilter(s)}>
                    {LEAD_STATE_LABEL[s]} ({c})
                  </Pill>
                );
              })}
            </div>
          </div>

          <div className="lead-rows">
          {shown.length === 0 ? (
            <EmptyNote title="No leads in this view">
              Nothing matches the current filter.
            </EmptyNote>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {shown.map((l) => {
                // §3 — flag a surfaced lead that is already an existing
                // client (phone/email match against the streamed masters).
                const clientMatch = matchLeadToClient(l, clientMasters);
                return (
                <Card
                  key={l.id}
                  className="row-card"
                  style={{
                    cursor: 'pointer',
                    outline:
                      (selectMode ? picked.has(l.id) : l.id === selectedId)
                        ? '2px solid var(--accent)'
                        : undefined,
                  }}
                >
                  <div
                    onClick={() =>
                      selectMode ? togglePick(l.id) : setSelectedId(l.id)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={picked.has(l.id)}
                        onChange={() => togglePick(l.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${l.name}`}
                      />
                    )}
                    <b>{l.name}</b>
                    <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                      {l.id}
                    </span>
                    <Tag>{LEAD_SOURCE_LABEL[l.source]}</Tag>
                    <Tag kind={l.state === 'rejected' ? 'warn' : 'tag'}>
                      {LEAD_STATE_LABEL[l.state]}
                    </Tag>
                    {clientMatch && (
                      <Tag
                        kind="val"
                        title={`This lead's ${clientMatch.via} matches existing client ${clientMatch.clientNo} (${clientMatch.name})`}
                      >
                        Existing client — {clientMatch.clientNo}
                      </Tag>
                    )}
                    {l.assignedTier && (
                      <Tag kind="val">
                        {l.assignedTier}
                        {l.quote ? ` · ${inr(l.quote.valueINR)}` : ''}
                      </Tag>
                    )}
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 12,
                        color: 'var(--ink-mute)',
                      }}
                    >
                      {leadAgeDays(l, nowISO())}d
                    </span>
                    {/* BUG 3 — per-row move-to-Trash. Admin-gated
                        (canManageTrash). Hidden in bulk-select mode (the
                        checkbox + bulk bar own removal there). */}
                    {mayTrash && !selectMode && (
                      <button
                        type="button"
                        className="lead-row-trash"
                        title="Move this lead to Trash"
                        aria-label={`Move ${l.name} to Trash`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmTrashId((id) =>
                            id === l.id ? null : l.id,
                          );
                        }}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                  {/* Inline per-row confirm — keeps the destructive action
                      one tap from the card but never one tap from a
                      mistake. */}
                  {mayTrash && !selectMode && confirmTrashId === l.id && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginTop: 8,
                        paddingTop: 8,
                        borderTop: '1px solid var(--rule)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: 'var(--ink-soft)' }}>
                        Move this lead to Trash? Fully recoverable from the
                        Trash screen.
                      </span>
                      <Btn3
                        intent="ghost"
                        size="sm"
                        onClick={() => setConfirmTrashId(null)}
                      >
                        Cancel
                      </Btn3>
                      <Btn3
                        intent="ink"
                        size="sm"
                        onClick={() => {
                          setConfirmTrashId(null);
                          void trashOne(l);
                        }}
                      >
                        Yes, move to Trash
                      </Btn3>
                    </div>
                  )}
                </Card>
                );
              })}
            </div>
          )}
          </div>
        </Box>
      </div>
      )}

      {/* ───── detail : triage (right pane on desktop; the WHOLE screen on
          mobile, with a back-to-list control — BUG 1) ───── */}
      {showDetail && (
      <div className="lead-detail">
        {/* Back-to-list — mobile only (CSS-gated). Returns to the inbox
            list by clearing the selection. */}
        <div className="lead-backbar">
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setSelectedId(null)}
          >
            ‹ Back to lead list
          </Btn3>
        </div>
        {selected ? (
          <LeadDetail
            key={selected.id}
            lead={selected}
            onChange={upsert}
            authorName={actorName}
            isTeamView={vis.seesReviewLayer}
            isAdmin={role === 'admin'}
            mayTrash={mayTrash}
            onTrash={trashOne}
            knownClients={knownClients}
            knownPartners={knownPartners}
            liveProjects={clientProjectsRaw}
            clientMatch={matchLeadToClient(selected, clientMasters)}
            contactPool={clientMasters}
          />
        ) : (
          <Box title="The lead record" note="Pick a lead on the left">
            <EmptyNote title="No lead selected">
              Select a lead from the inbox to open its record. The record is
              the primary surface — chat assists, never the boss.
            </EmptyNote>
          </Box>
        )}
      </div>
      )}
    </div>
  );
}

/* ════════════════════ Lead detail / triage ════════════════════ */

/** Exported for the Module-1 RENDERED-screen Playwright harness (dev only). */
export function LeadDetail({
  lead,
  onChange,
  authorName,
  isTeamView,
  isAdmin,
  mayTrash,
  onTrash,
  knownClients,
  knownPartners,
  liveProjects,
  clientMatch,
  contactPool = [],
}: {
  lead: Lead;
  onChange: (l: Lead) => void;
  authorName: string;
  isTeamView: boolean;
  isAdmin: boolean;
  /** true → the signed-in user may move the lead to the recoverable Trash. */
  mayTrash: boolean;
  /** Move this lead to the recoverable Trash. */
  onTrash: (l: Lead) => void;
  knownClients: ClientRow[];
  knownPartners: LivePartner[];
  /** Live `projects/` — scopes the inline Record-payment modal to THIS lead's
   *  converted project (Module 1: payment recorded on the same record). */
  liveProjects: LiveProject[];
  clientMatch: ClientMatch | null;
  /** Contact spine (AD-039) — the live `clients/` masters, used to re-match
   *  at convert time and to resolve the linked-contact banner / chip. */
  contactPool?: LiveClient[];
}) {
  const [concern, setConcern] = useState(lead.concern);
  const [propertyType, setPropertyType] = useState(lead.propertyType);
  const [sqft, setSqft] = useState<string>(
    lead.sqft != null ? String(lead.sqft) : '',
  );
  const [msg, setMsg] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  // MODULE 1 — the inline Record-a-payment modal (scoped to this lead's
  // converted project, so money is recorded on the SAME record, no jump).
  const [payOpen, setPayOpen] = useState(false);
  // M5-gaps ITEM 2 — after a successful convert, OFFER (don't force) sending
  // the intake form. `postConvert` holds the new project's pid + the client's
  // cid/name so the offer modal can open IntakeLinkSendBar without re-querying.
  // `intakeOpen` then mounts the SendBar (its own service selector, L1A default).
  const [postConvert, setPostConvert] = useState<{
    pid: string;
    cid: string;
    clientName: string;
  } | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  // IA FIX (spec Q7) — converting to a NEW client or an EXISTING one.
  const [convertMode, setConvertMode] = useState<'new' | 'existing'>('new');
  const [clientSearch, setClientSearch] = useState('');
  const [pickedClientId, setPickedClientId] = useState<string | null>(null);
  // The new project's OWN name (2026-05-22) — its identity, distinct from
  // the client and the VP code. Blank → the `<tier> — <client>` default.
  const [projectName, setProjectName] = useState('');
  // PARTNER SPRINT — optional channel partner on convert ('' = none).
  const [pickedPartnerId, setPickedPartnerId] = useState('');
  // P3 — confirm-gated archive (status flip, staff-allowed) + admin-only
  // identity edit (name/phone/email; rules whitelist these for admin only).
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [eName, setEName] = useState(lead.name);
  const [ePhone, setEPhone] = useState(lead.phone ?? '');
  const [eEmail, setEEmail] = useState(lead.email ?? '');
  const [savingId, setSavingId] = useState(false);
  // Owner bug 2026-07-03 ("the Name field appends"): when a HOST keeps this
  // component mounted across lead switches (the Lenses drawer did, pre-key),
  // these drafts stayed seeded from the PREVIOUS lead and typing appended
  // onto the stale value. Re-seed whenever the lead SUBJECT changes — keyed
  // on lead.id only, so listener echoes of the SAME lead never clobber
  // in-progress typing. (Belt-and-braces: the drawer now also remounts via
  // key={lead.id}.)
  useEffect(() => {
    setEName(lead.name);
    setEPhone(lead.phone ?? '');
    setEEmail(lead.email ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);
  const navigate = useNavigate();

  // Consult-scheduling Door A — flag-gated (settings/flags.consulting, default
  // OFF). When ON for the signed-in uid, the "Book & discovery" block swaps the
  // v1 status+date buttons for the live <BookConsultPanel>. OFF → unchanged.
  const { user } = useAuth();
  const [consultingEnabled, setConsultingEnabled] = useState(false);
  useEffect(
    () => subscribeConsultingFlag(user?.uid ?? null, setConsultingEnabled),
    [user?.uid],
  );

  /* ── Contact spine (AD-039) — linked-contact banner + possible-match chip ──
   * `lead.contactId` (when set) is the canonical link → render the banner.
   * Otherwise surface the strongest stored candidate that the staff hasn't
   * dismissed → the staff-confirm chip (Link / Dismiss). Owner-locked chip
   * copy: "Possible match: <Name> · <C#####> (<reason>) · Link / Dismiss". */
  const linkedContact = useMemo(() => {
    if (!lead.contactId) return null;
    const c = contactPool.find((x) => x.cid === lead.contactId);
    return {
      cid: lead.contactId,
      clientNo: c?.clientNo ?? lead.contactId,
      name: c?.name ?? '(contact)',
      trashed: c?.trashed === true,
    };
  }, [lead.contactId, contactPool]);

  const suggestion = useMemo<MatchResult | null>(() => {
    if (lead.contactId) return null; // already linked — no chip
    const dismissed = new Set(lead.dismissedMatchIds ?? []);
    const cands = (lead.matchCandidates ?? []).filter(
      (c) => !dismissed.has(c.contactId),
    );
    if (cands.length === 0) return null;
    const top = cands[0];
    return {
      contactId: top.contactId,
      clientNo: top.clientNo,
      name: top.name,
      score: top.score,
      reasons: [],
    };
  }, [lead.contactId, lead.matchCandidates, lead.dismissedMatchIds]);

  /** Plain-language reason for the chip, derived from the stored score. */
  function matchReason(score: number): string {
    if (score >= 1.0) return 'phone';
    if (score >= 0.9) return 'email';
    if (score >= 0.6) return 'name + phone';
    return 'name';
  }

  /** Link the lead to a suggested contact (staff confirm). */
  async function linkContact(cid: string) {
    const next: Lead = {
      ...lead,
      contactId: cid,
      contactLinkSource: 'manual',
      updatedAt: nowISO(),
    };
    onChange(next);
    try {
      await appendLeadRef(cid, lead.id, authorName);
    } catch {
      /* reverse link is convenience; the lead.contactId persist owns truth */
    }
    setMsg(`Linked to contact ${cid}.`);
  }

  /** Dismiss a suggestion so the chip stops nagging. */
  function dismissMatch(cid: string) {
    const next: Lead = {
      ...lead,
      dismissedMatchIds: [...(lead.dismissedMatchIds ?? []), cid],
      updatedAt: nowISO(),
    };
    onChange(next);
    setMsg('Suggestion dismissed.');
  }

  /* ── Contact spine (AD-039) — MANUAL link / change / unlink (staff override)
   * The gap: auto-match + suggestion only fire on an exact phone/email match.
   * A returning client on a BRAND-NEW number AND email has no match at all —
   * but staff KNOW it's the same person. This is the manual path: search ALL
   * contacts (ClientPicker) and link by hand. A manual link uses source
   * 'manual' so it WINS over any auto-match (mirrors the suggestion handler
   * `linkContact` above — same contactId / appendLeadRef / persist path). */
  const [manualLinkOpen, setManualLinkOpen] = useState(false);

  /** Manually link the lead to a chosen contact (staff override). Reuses the
   *  SAME write path as the suggestion-chip `linkContact` (source 'manual'). */
  async function manualLink(cid: string) {
    setManualLinkOpen(false);
    if (!cid || cid === lead.contactId) return;
    await linkContact(cid);
  }

  /** Clear a wrong link. Leaves the contact's reverse `leadRefs[]` back-ref as
   *  is (a stale back-ref is harmless; there is no removeLeadRef helper and the
   *  rules whitelist does not need one — the lead.contactId is the truth). */
  function unlinkContact() {
    const next: Lead = {
      ...lead,
      contactId: null,
      contactLinkSource: null,
      updatedAt: nowISO(),
    };
    onChange(next);
    setManualLinkOpen(false);
    setMsg('Contact unlinked.');
  }

  const archivable = nextStates(lead.state).includes('parked');
  const isArchived = lead.state === 'parked' || lead.state === 'rejected';

  function archiveLead() {
    try {
      onChange(transitionLead(lead, 'parked', nowISO()));
      setMsg('Lead archived (parked — reversible, never deleted).');
      setConfirmArchive(false);
    } catch (e) {
      setMsg((e as Error).message);
    }
  }

  async function saveIdentity() {
    if (savingId) return;
    setSavingId(true);
    try {
      const next = await updateLeadAdmin(
        lead,
        { name: eName.trim(), phone: ePhone.trim(), email: eEmail.trim() },
        authorName,
        nowISO(),
      );
      // local echo (listener reconciles via the refreshed snapshot)
      onChange(next);
      setMsg('Lead identity corrected (admin).');
    } catch (e) {
      setMsg(`Identity save failed: ${(e as Error).message}`);
    } finally {
      setSavingId(false);
    }
  }

  const moves = nextStates(lead.state);

  function go(to: LeadState) {
    try {
      onChange(transitionLead(lead, to, nowISO()));
      setMsg(`Moved to "${LEAD_STATE_LABEL[to]}".`);
    } catch (e) {
      setMsg((e as Error).message);
    }
  }

  function saveBasics() {
    onChange({
      ...lead,
      concern: concern.trim(),
      propertyType: propertyType.trim(),
      sqft: sqft.trim() ? Number(sqft) : null,
      updatedAt: nowISO(),
    });
    setMsg('Concern & property basics saved.');
  }

  // STREAMLINE 1b/1e (2026-07-03) — booking / discovery go through the ONE
  // state machine's event seams (`recordConsultBooked` / `recordDiscoveryDone`,
  // legal `transitionLead` hops only). The old `transitionLeadSafe` swallowed
  // an illegal hop and date-stamped anyway — which is exactly how the record
  // could read "Discovery done" while the board column still said
  // "Consult booked" (the audit's vocabulary-drift example). Now the stamp
  // and the stage move together; a later-stage lead only gets the date stamp
  // (never dragged backward).
  function bookConsult() {
    const now = nowISO();
    const next = recordConsultBooked(lead, now, now);
    onChange(next);
    setMsg(
      next.state === 'consult_booked'
        ? 'Consult booked — stage moved to Consult booked (v1: date recorded).'
        : 'Consult date recorded (stage unchanged — the lead is already past booking).',
    );
  }

  function discoveryDone() {
    const now = nowISO();
    // Phase 2c — the call notes typed below ride the SAME event save (one
    // click records the discovery AND its minutes; no second Save needed).
    const next: Lead = {
      ...recordDiscoveryDone(lead, now, now),
      ...(callNotes.trim() !== (lead.callNotes ?? '')
        ? { callNotes: callNotes.trim() || null }
        : {}),
    };
    onChange(next);
    setMsg(
      next.state === 'discovery_done'
        ? 'Discovery recorded — stage moved to Discovery done.'
        : 'Discovery date recorded (stage unchanged — the lead is already past discovery).',
    );
  }

  // BREAK-3 (linkage audit 2026-07-03) — the lead's open SENT quote (id +
  // total): SetPrice locks hand edits + shows the reconciliation note while
  // one is out with the client (accepting it overwrites the agreed price via
  // priceLeadFromQuote). Best-effort read; null on error keeps SetPrice open.
  const [openSentQuote, setOpenSentQuote] = useState<{
    id: string;
    total: number;
    currency: 'INR' | 'USD';
  } | null>(null);
  useEffect(() => {
    let alive = true;
    setOpenSentQuote(null);
    void findOpenSentQuoteForLead(lead.id)
      .then((q) => alive && setOpenSentQuote(q))
      .catch(() => alive && setOpenSentQuote(null));
    return () => {
      alive = false;
    };
    // re-check when the lead's send/state audit moves (a send or accept
    // changes the open-quote reality).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.sentAt, lead.state]);

  // Phase 2c (2026-07-03) — minutes/notes of the discovery call. THE input the
  // customized quote is built from (the quote composer surfaces them when it
  // opens pre-filled from this lead). Saved via the existing updateLead seam
  // (`onChange`), snapshot-carried — no rules change.
  const [callNotes, setCallNotes] = useState(lead.callNotes ?? '');
  useEffect(() => {
    setCallNotes(lead.callNotes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);
  function saveCallNotes() {
    onChange({ ...lead, callNotes: callNotes.trim() || null, updatedAt: nowISO() });
    setMsg('Call notes saved — the quote composer will show them.');
  }

  async function doConvert() {
    if (converting) return;
    setConverting(true);
    // STREAMLINE 1d (2026-07-03) — the pre-flight is the ONE lib predicate
    // `convertGate` (agreed price + 6 must-haves + already-converted + the
    // open-sent-quote guard), replacing the bare `isLeadComplete` check. A
    // lead with an OPEN SENT quote must be resolved through the quote
    // (Accept — which itself converts — or void it), never converted around
    // it. The HARD guard lives in `convertLeadPersist`; this pre-flight only
    // gives the plain-language message early. A failed quote lookup here
    // degrades to null (the hard guard stays authoritative, fail-closed).
    let openSentQuoteId: string | null = null;
    try {
      openSentQuoteId = await findOpenSentQuoteIdForLead(lead.id);
    } catch {
      /* pre-flight only — convertLeadPersist re-checks and fails closed */
    }
    const gate = convertGate(lead, openSentQuoteId);
    if (!gate.ok) {
      setMsg(
        gate.reason ??
          'Complete the must-haves above (service + agreed price) to convert.',
      );
      setConverting(false);
      return;
    }
    // Contact spine (AD-039) — resolve the link FIRST. If the lead is already
    // linked, OR an exact phone/email re-match (data may have improved since
    // creation) finds a contact, convert attaches to the EXISTING contact and
    // mints NO new C#####. Otherwise fall through to the IA-fix picker / new.
    let linkCid: string | null = lead.contactId ?? null;
    if (!linkCid) {
      const auto = bestAutoLink(
        matchContacts(
          { name: lead.name, phone: lead.phone, email: lead.email },
          contactPool as unknown as ContactLite[],
        ),
      );
      linkCid = auto?.contactId ?? null;
    }

    // IA FIX (Q7) — resolve the convert target: a brand-new client, or an
    // existing one (spine-linked, or the converter picked from the directory).
    let target: ConvertClientTarget;
    if (linkCid) {
      const c = contactPool.find((x) => x.cid === linkCid);
      // A trashed linked contact falls back to mint (edge case §13).
      if (c && !c.trashed) {
        target = {
          mode: 'existing',
          clientId: c.cid,
          clientNo: c.clientNo,
          clientName: c.name,
        };
      } else {
        target = { mode: 'new' };
      }
    } else if (convertMode === 'existing') {
      const picked = knownClients.find((c) => c.clientId === pickedClientId);
      if (!picked) {
        setMsg('Pick an existing client first, or switch to "New client".');
        setConverting(false);
        return;
      }
      target = {
        mode: 'existing',
        clientId: picked.clientId,
        clientNo: picked.clientNo,
        // The picked row's name is the CANONICAL master name (mergeClientRows
        // makes the master win) — seed the new project's clientName cache
        // from it so converting onto an existing client never drifts.
        clientName: picked.name,
      };
    } else {
      target = { mode: 'new' };
    }
    try {
      // Mints a fresh VP### (independent counter) + writes real
      // projects/{pid} + creation ledger; a new client also writes
      // clients/{cid}. An existing client's master is left untouched.
      // The optional channel partner ('' → null) is written to
      // project.partnerId so the Partner Portfolio finds the project.
      const res = await convertLeadPersist(
        lead,
        authorName,
        nowISO(),
        target,
        pickedPartnerId || null,
        projectName.trim(),
      );
      // Persist the converted (terminal) lead through the whitelisted
      // leadStore path so the inbox reflects the conversion + ids.
      onChange(res.lead);
      setMsg(
        `Converted → project ${res.projectId} · ` +
          `${res.newClientCreated ? 'new client' : 'existing client'} ` +
          `${res.clientNo}.`,
      );
      // M5-gaps ITEM 2 — don't navigate immediately. OFFER to send the intake
      // form first (the natural next step on a fresh project). `res.clientId`
      // is the new/attached client's cid (= clientNo for a new client). The
      // modal's [Not Now] navigates to the canonical project-detail route.
      setPostConvert({
        pid: res.projectId,
        cid: res.clientId,
        clientName: lead.name,
      });
    } catch (e) {
      setMsg(`Convert failed: ${(e as Error).message}`);
    } finally {
      setConverting(false);
    }
  }

  /* AI→data: a chat proposal's patch is mapped onto the fallback form /
     the lead. Chat is primary; this is the bridge to the same writes the
     form does — no second logic path (still goes through the pure
     leads.ts mutators for any state change). */
  function applyChatPatch(patch: Record<string, unknown>) {
    if (typeof patch.sqft === 'number') {
      setSqft(String(patch.sqft));
      setMsg(`Chat → area set to ${patch.sqft} sqft (review & Save basics).`);
    }
    if (typeof patch.propertyType === 'string') {
      setPropertyType(patch.propertyType);
      setMsg(`Chat → property type "${patch.propertyType}" (review & Save).`);
    }
    if (typeof patch.desiredTier === 'string') {
      // Option A confirm-loop: the chat proposes the service; applying it
      // writes the assigned tier onto the RECORD (the truth), which the
      // Set-price panel then reads. Nothing is sent to a client here.
      onChange({
        ...lead,
        assignedTier: patch.desiredTier as Tier,
        desiredTier: (lead.desiredTier ?? patch.desiredTier) as Tier,
        updatedAt: nowISO(),
      });
      setMsg(
        `Chat → service ${patch.desiredTier} set on the record — set the agreed price below.`,
      );
    }
    if (typeof patch.concernAppend === 'string') {
      setConcern((c) =>
        c ? `${c}\n${patch.concernAppend as string}` : (patch.concernAppend as string),
      );
      setMsg('Chat → captured into the concern (review & Save basics).');
    }
    if (patch.action === 'qualify') {
      try {
        onChange(transitionLead(lead, 'qualified', nowISO()));
        setMsg('Chat → lead moved to Qualified.');
      } catch (e) {
        setMsg((e as Error).message);
      }
    }
    if (patch.action === 'book_consult') {
      bookConsult();
    }
  }

  const convertedPid = lead.convertedProjectId ?? null;
  const convertedProject =
    convertedPid != null
      ? liveProjects.find((p) => p.pid === convertedPid) ?? null
      : null;

  // NEW-LEAD IDENTITY FIRST (owner 2026-07-03: the capture sat "really deep
  // down below the scroll"). ONE block, rendered in one of two slots: at the
  // TOP of the record while the lead has no phone AND no email (the blank
  // manual-entry signature — capture who they are before anything else), and
  // back in its usual quiet admin corner once identity is saved. REORDERING
  // (not auto-scroll+focus) is the calmer fix: the record opens already
  // showing the field — no scroll-jump or focus-steal side effects, and the
  // order flips only on the user's own Save, never mid-reading.
  const identityFirst = needsIdentityCapture(lead);
  const identityEditEl = isAdmin ? (
    <div
      style={
        identityFirst
          ? {
              border: '1px solid var(--accent, #008069)',
              borderRadius: 'var(--r-sm, 9px)',
              background: 'var(--accent-wash, #E9F7EF)',
              padding: '12px 14px',
            }
          : undefined
      }
    >
      <Lbl>
        {identityFirst
          ? 'New lead — capture identity first (admin)'
          : 'Edit lead details (admin only)'}
      </Lbl>
      <input
        value={eName}
        onChange={(e) => setEName(e.target.value)}
        placeholder="Name"
        style={inputStyle}
        aria-label="Lead name"
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 8,
          flexWrap: 'wrap',
        }}
      >
        <input
          value={ePhone}
          onChange={(e) => setEPhone(e.target.value)}
          placeholder="Phone"
          style={{ ...inputStyle, flex: 1, minWidth: 160 }}
          aria-label="Lead phone"
        />
        <input
          value={eEmail}
          onChange={(e) => setEEmail(e.target.value)}
          placeholder="Email"
          style={{ ...inputStyle, flex: 1, minWidth: 160 }}
          aria-label="Lead email"
        />
        <Btn3
          intent="grn"
          size="sm"
          onClick={saveIdentity}
          disabled={savingId}
        >
          {savingId ? 'Saving…' : 'Save identity'}
        </Btn3>
      </div>
      <p
        style={{
          fontSize: 11.5,
          color: 'var(--ink-mute)',
          marginTop: 6,
        }}
      >
        {identityFirst
          ? 'Who is this lead? Name + phone (or email) first — everything below builds on it.'
          : 'Identity fields are admin-only by Firestore rule. Staff can triage status/notes but not edit name/phone/email.'}
      </p>
    </div>
  ) : null;

  return (
    <div className="lead-record-grid">
      {/* ═════ PRIMARY SURFACE — the LEAD RECORD (Option A: the truth) ═════ */}
      <div
        className="lead-record-main"
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {/* The 4-step flow status — Lead in ▸ Quote ▸ Payment ▸ Convert. One
            continuous record; no manual jumps. */}
        <FlowStatusBand
          lead={lead}
          convertedProject={convertedProject}
          clientMatch={clientMatch}
        />

        {/* SUBMISSION DETAILS (2026-06-21) — "what the client actually told
            us" off the website / free-tool form: the contact message, the E2
            score + observations + room zones + uploaded map, the E4 area
            scores + flags, guide requested, etc. Renders nothing for a
            portal-native / WhatsApp lead (no submission payload). This is the
            detail the legacy LeadDetail screen showed; the engagement-flow
            redesign had dropped it. */}
        <SubmissionDetails lead={lead} />

        {/* Trash (admin) — kept as a quiet record action, top-right. */}
        {mayTrash && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {!confirmTrash ? (
              <Btn3
                intent="ghost"
                size="sm"
                onClick={() => setConfirmTrash(true)}
                title="Move this lead to the recoverable Trash"
              >
                🗑 Move lead to Trash
              </Btn3>
            ) : (
              <>
                <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  Move this lead to Trash? Fully recoverable.
                </span>
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => setConfirmTrash(false)}
                >
                  Cancel
                </Btn3>
                <Btn3
                  intent="ink"
                  size="sm"
                  onClick={() => {
                    setConfirmTrash(false);
                    onTrash(lead);
                  }}
                >
                  Yes, move to Trash
                </Btn3>
              </>
            )}
          </div>
        )}

        {/* The structured record — this is the PRIMARY working surface now
            (Option A). Chat assists from the side panel; it only proposes. */}
        <Box
          title="The lead record"
          note={`${LEAD_SOURCE_LABEL[lead.source]} · ${LEAD_STATE_LABEL[lead.state]} · ${lead.id}`}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* NEW-LEAD slot — identity capture FIRST until saved (see
                identityEditEl above). */}
            {identityFirst && identityEditEl}
            <Card flat>
              <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.7 }}>
                <b style={{ color: 'var(--ink)' }}>{lead.name}</b>
                <br />
                {lead.phone || '—'} · {lead.email || '—'} · {lead.location || '—'}
                <br />
                Owner: {lead.ownerName}
                {clientMatch && (
                  <>
                    <br />
                    <Tag
                      kind="val"
                      title={`This lead's ${clientMatch.via} matches existing client ${clientMatch.clientNo} (${clientMatch.name})`}
                    >
                      Existing client — {clientMatch.clientNo} ·{' '}
                      {clientMatch.name} (matched on {clientMatch.via})
                    </Tag>
                  </>
                )}
                {lead.convertedProjectId && (
                  <>
                    <br />
                    <Tag kind="val">
                      Converted → {lead.convertedProjectId} ·{' '}
                      {lead.convertedClientNo}
                    </Tag>
                  </>
                )}
              </div>
            </Card>

            {/* ── Contact spine (AD-039) — linked-contact banner ──
                Shown when the lead is linked to a clients/{cid}. Vibrant
                green wash on the pure-white canvas; click → open contact. */}
            {linkedContact && (
              <div
                data-testid="linked-contact-banner"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 13px',
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid #bfe6cd',
                  background: '#e8f6ed',
                  fontSize: 13,
                  color: '#155e35',
                }}
              >
                <span style={{ fontWeight: 700 }}>Linked to</span>
                <span>
                  {linkedContact.name} · {linkedContact.clientNo}
                  {linkedContact.trashed ? ' (in Trash)' : ''}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/contacts/${encodeURIComponent(linkedContact.cid)}`)
                  }
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: 'none',
                    color: '#0b7a3b',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: 13,
                    padding: 0,
                  }}
                >
                  Open contact
                </button>
              </div>
            )}

            {/* ── Contact spine (AD-039) — possible-match chip (staff confirm) ──
                Owner-locked copy: "Possible match: <Name> · <C#####> (<reason>)
                · Link / Dismiss". Vibrant amber wash; never an auto-link. */}
            {suggestion && (
              <div
                data-testid="possible-match-chip"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '9px 13px',
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid #ecd9a6',
                  background: '#fbf2dd',
                  fontSize: 13,
                  color: '#7a5b00',
                }}
              >
                <span>
                  <b>Possible match:</b> {suggestion.name} ·{' '}
                  {suggestion.clientNo} ({matchReason(suggestion.score)})
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    data-testid="possible-match-link"
                    onClick={() => void linkContact(suggestion.contactId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0b7a3b',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    Link
                  </button>
                  <button
                    type="button"
                    data-testid="possible-match-dismiss"
                    onClick={() => dismissMatch(suggestion.contactId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#8a6d1a',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 13,
                      padding: 0,
                    }}
                  >
                    Dismiss
                  </button>
                </span>
              </div>
            )}

            {/* ── Contact spine (AD-039) — MANUAL link to a contact (staff
                override) ── Always available, match or no match: the whole
                point is the returning-client-on-a-new-number case where auto-
                match + the suggestion chip find NOTHING. "Link to a contact"
                (or "Change contact" when already linked) opens ClientPicker to
                search ALL contacts by name / C##### / phone / email; picking
                one links it with source 'manual' (wins over any auto-match).
                "Unlink" clears a wrong link. */}
            <div data-testid="manual-link-block">
              <Lbl>Contact link</Lbl>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <Btn3
                  intent={linkedContact ? 'ghost' : 'au'}
                  size="sm"
                  data-testid="manual-link-open"
                  onClick={() => setManualLinkOpen((o) => !o)}
                  title={
                    linkedContact
                      ? 'Link this lead to a different contact'
                      : 'Manually link this lead to an existing contact (when no phone/email match exists)'
                  }
                >
                  {manualLinkOpen
                    ? 'Cancel'
                    : linkedContact
                      ? '⇄ Change contact'
                      : '🔗 Link to a contact'}
                </Btn3>
                {linkedContact && !manualLinkOpen && (
                  <Btn3
                    intent="ghost"
                    size="sm"
                    data-testid="manual-link-unlink"
                    onClick={unlinkContact}
                    title="Clear this contact link"
                  >
                    Unlink
                  </Btn3>
                )}
                {!linkedContact && !manualLinkOpen && (
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    Same person on a new number/email? Link them by hand —
                    search any contact.
                  </span>
                )}
              </div>
              {manualLinkOpen && (
                <div style={{ marginTop: 8 }} data-testid="manual-link-picker">
                  <ClientPicker
                    value={lead.contactId ?? ''}
                    directoryOverride={knownClients}
                    ariaLabel="Search and link this lead to a contact"
                    placeholder="Search any contact — name, C#####, phone or email…"
                    onPick={(c) => void manualLink(c.clientId)}
                  />
                </div>
              )}
            </div>

            <div>
              <Lbl>Qualify</Lbl>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {moves.includes('qualifying') && (
                  <Btn3 intent="ink" size="sm" onClick={() => go('qualifying')}>
                    Start qualifying
                  </Btn3>
                )}
                {moves.includes('qualified') && (
                  <Btn3 intent="grn" size="sm" onClick={() => go('qualified')}>
                    Accept → Qualified
                  </Btn3>
                )}
                {moves.includes('parked') && (
                  <Btn3 intent="ghost" size="sm" onClick={() => go('parked')}>
                    Park
                  </Btn3>
                )}
                {moves.includes('rejected') && (
                  <Btn3 intent="ghost" size="sm" onClick={() => go('rejected')}>
                    Reject
                  </Btn3>
                )}
                {moves.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    Terminal state — the project pipeline owns it from here.
                  </span>
                )}
              </div>
            </div>

            <div>
              <Lbl>Concern &amp; property basics</Lbl>
              <textarea
                value={concern}
                onChange={(e) => setConcern(e.target.value)}
                rows={2}
                placeholder="What does the client want? What's the concern?"
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <input
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  placeholder="Property type (residence / commercial …)"
                  style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                />
                <input
                  value={sqft}
                  onChange={(e) => setSqft(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="Sqft (computed tiers)"
                  style={{ ...inputStyle, width: 160 }}
                />
                <Btn3 intent="ghost" size="sm" onClick={saveBasics}>
                  Save basics
                </Btn3>
              </div>
            </div>

            <div>
              <Lbl>Book &amp; discovery</Lbl>
              {/* Phase 2 (2026-07-03): a REAL booked consult carries a Meet
                  link on the lead — surface it as a working link. */}
              {lead.consultMeetLink && (
                <div style={{ marginBottom: 8, fontSize: 12.5 }}>
                  <a
                    href={lead.consultMeetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent)', fontWeight: 600 }}
                  >
                    Google Meet link ↗
                  </a>{' '}
                  <span style={{ color: 'var(--ink-mute)' }}>
                    (consult {lead.consultBookedAt ?? 'booked'})
                  </span>
                </div>
              )}
              {consultingEnabled && (
                <BookConsultPanel
                  lead={{
                    id: lead.id,
                    name: lead.name,
                    city: lead.location,
                    projectType: lead.propertyType,
                  }}
                  hostName="Rohit"
                />
              )}
              {/* Phase 2 rule: the v1 date-stamp door STAYS available even
                  with the live panel on — the booking callable can come back
                  `not_connected` (calendar setup pending) and the manual
                  stamp must never become a dead end. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: consultingEnabled ? 8 : 0 }}>
                {consultingEnabled && (
                  <span style={{ flexBasis: '100%', fontSize: 11.5, color: 'var(--ink-mute)' }}>
                    Manual fallback — stamps the date only (no calendar event):
                  </span>
                )}
                <Btn3 intent="ghost" size="sm" onClick={bookConsult}>
                  Book consult
                  {lead.consultBookedAt ? ` ✓ ${lead.consultBookedAt}` : ''}
                </Btn3>
                <Btn3 intent="ghost" size="sm" onClick={discoveryDone}>
                  Discovery done
                  {lead.discoveryDoneAt ? ` ✓ ${lead.discoveryDoneAt}` : ''}
                </Btn3>
              </div>

              {/* Phase 2c — the discovery call's minutes/notes. THE input the
                  customized quote is built from: the quote composer shows these
                  when it opens pre-filled from this lead. "Discovery done"
                  saves them in the same click; the Save button covers edits
                  made later. */}
              <div style={{ marginTop: 10 }}>
                <label
                  htmlFor={`call-notes-${lead.id}`}
                  style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)', margin: '0 0 4px' }}
                >
                  Call notes (what was discussed)
                </label>
                <textarea
                  id={`call-notes-${lead.id}`}
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={3}
                  placeholder="Minutes of the discovery call — needs, constraints, promises. The custom quote is priced from this."
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={saveCallNotes}
                    disabled={callNotes.trim() === (lead.callNotes ?? '')}
                  >
                    Save call notes
                  </Btn3>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                    Shown in the quote composer when you create the quote from
                    this lead.
                  </span>
                </div>
              </div>
            </div>

            {/* SET PRICE — the Quote module's heart (MOCKUP_2). The agreed
                price set here is the single source of truth: on Convert it
                seeds the project's gateAmounts (the $40 / Tarveen fix at the
                root). `onSave` persists through the SAME `onChange` →
                updateLead path the rest of this screen uses. */}
            <SetPrice
              lead={lead}
              openSentQuote={openSentQuote}
              onSave={onChange}
              onSyncConvertedProject={
                lead.convertedProjectId
                  ? (input) => {
                      // BUG B — this lead is already a project; route the price
                      // edit to the project's agreed price so it appends to the
                      // ONE shared change-history log (`feeHistory`). The store
                      // skips the write on a no-op (same currency + amount).
                      void setProjectAgreedPrice(
                        lead.convertedProjectId as string,
                        {
                          tier: input.tier,
                          currency: input.currency,
                          value: input.value,
                          prior: input.prior,
                          gst: input.gst,
                          note: 'Edited from the lead (Set price)',
                        },
                        authorName,
                      ).catch(() => {
                        /* lead save already succeeded; surface nothing extra */
                      });
                    }
                  : undefined
              }
            />

            {/* ═════ PAYMENT — on the SAME record, after Convert (Module 1) ═════
                Once the lead is a project, record the payment against the
                money gate right here — no jump to the Money screen. */}
            {lead.state === 'converted' && (
              <div>
                <Lbl>Payment</Lbl>
                {convertedProject ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Btn3
                      intent="grn"
                      size="sm"
                      onClick={() => setPayOpen(true)}
                    >
                      ⟳ Record a payment
                    </Btn3>
                    <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                      Records against this project’s money gate ·{' '}
                      {lead.convertedProjectId}. Real Razorpay auto-mark lands in
                      Module 8.
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    Project {lead.convertedProjectId} is being loaded — the
                    Record-payment control appears once its money gate streams in.
                  </span>
                )}
              </div>
            )}

            {/* ═════ CONVERT TO PROJECT ═════
                Module 1 (Option A): the gate is the 6 must-haves + the AGREED
                PRICE (shown live in the Set-price panel above) — NOT a separate
                "Lock quote" tap. Convert is offered the moment the record is
                complete; a half-filled record shows what's still needed. */}
            <div>
              <Lbl>Convert to project</Lbl>
              {lead.state === 'converted' ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--good)' }}>
                    ✓ Converted → {lead.convertedProjectId} ·{' '}
                    {lead.convertedClientNo}.
                  </span>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(`/conversation/${lead.convertedProjectId}`)
                    }
                  >
                    Open the project →
                  </Btn3>
                </div>
              ) : isLeadComplete(lead) ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  {/* IA FIX Q7 — new client OR an existing one. */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Pill
                      on={convertMode === 'new'}
                      onClick={() => setConvertMode('new')}
                    >
                      New client
                    </Pill>
                    <Pill
                      on={convertMode === 'existing'}
                      onClick={() => setConvertMode('existing')}
                    >
                      Existing client
                    </Pill>
                  </div>

                  {/* Contact spine (AD-039) — convert preview. When the lead is
                      LINKED, convert reuses the existing contact (no new
                      C#####); otherwise a new contact is minted. */}
                  {linkedContact && !linkedContact.trashed ? (
                    <div
                      style={{
                        fontSize: 12.5,
                        color: 'var(--grn-ink, #1d7a44)',
                        fontWeight: 600,
                      }}
                    >
                      Will link to existing contact{' '}
                      <b>
                        {linkedContact.name} · {linkedContact.clientNo}
                      </b>{' '}
                      (no new client number).
                    </div>
                  ) : (
                    !linkedContact &&
                    convertMode === 'new' && (
                      <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
                        New contact — a fresh <b>C#####</b> will be created.
                      </div>
                    )
                  )}

                  {/* If the lead is recognised as an existing client, pre-suggest
                      that path (Sumit C00407 — no duplicate). */}
                  {clientMatch && !linkedContact && convertMode === 'new' && (
                    <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                      This contact matches existing client{' '}
                      <b>{clientMatch.clientNo}</b> ({clientMatch.name}) — switch
                      to <b>Existing client</b> to avoid a duplicate.
                    </div>
                  )}

                  {convertMode === 'existing' && (
                    <ExistingClientPicker
                      knownClients={knownClients}
                      search={clientSearch}
                      onSearch={setClientSearch}
                      pickedClientId={pickedClientId}
                      onPick={setPickedClientId}
                    />
                  )}

                  {/* The new project's OWN name — name it at conversion so
                      it is easy to identify (a client may have several
                      same-tier projects). Blank → a `<tier> — <client>`
                      default is written; the name stays editable later. */}
                  <input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder='Project name (optional) — e.g. "Residence — Vastu review"'
                    aria-label="Project name"
                    style={inputStyle}
                  />

                  {/* PARTNER SPRINT — optional channel partner. A project
                      may have a partner, or none (spec §3). */}
                  <select
                    value={pickedPartnerId}
                    onChange={(e) => setPickedPartnerId(e.target.value)}
                    aria-label="Channel partner (optional)"
                    style={inputStyle}
                  >
                    <option value="">
                      No channel partner (direct AV engagement)
                    </option>
                    {knownPartners.map((p) => (
                      <option key={p.pid} value={p.pid}>
                        {p.name || p.partnerNo} ({p.partnerNo})
                      </option>
                    ))}
                  </select>

                  <Btn3
                    intent="au"
                    onClick={doConvert}
                    disabled={
                      converting ||
                      (convertMode === 'existing' && !pickedClientId)
                    }
                  >
                    {converting
                      ? 'Converting…'
                      : convertMode === 'new'
                        ? '⟡ Convert — new client + project + money gate'
                        : '⟡ Convert — project for the selected client'}
                  </Btn3>
                </div>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  {completionMessage(lead) ??
                    'Complete the must-haves above to convert.'}{' '}
                  Convert seeds the money gate from the agreed price
                  (money-before-delivery).
                </span>
              )}
            </div>

            <div>
              <Lbl>Archive lead</Lbl>
              {isArchived ? (
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  Already {LEAD_STATE_LABEL[lead.state]} — leads are never
                  hard-deleted (audit trail). Re-qualify from the buttons
                  above to restore.
                </span>
              ) : !confirmArchive ? (
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => setConfirmArchive(true)}
                  disabled={!archivable}
                  title={
                    archivable
                      ? 'Archive (park) this lead'
                      : 'This lead cannot be parked from its current state'
                  }
                >
                  Archive…
                </Btn3>
              ) : (
                <span
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: 'var(--ink-soft)' }}>
                    Archive this lead? It is parked (reversible), never
                    deleted.
                  </span>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() => setConfirmArchive(false)}
                  >
                    Cancel
                  </Btn3>
                  <Btn3 intent="ink" size="sm" onClick={archiveLead}>
                    Yes, archive
                  </Btn3>
                </span>
              )}
            </div>

            {/* MOVE TO TRASH — the action now lives in the detail HEADER
                (BUG 3, discoverable) and on every lead card. This box no
                longer carries a buried duplicate. */}

            {/* Identity edit — its quiet usual slot once identity is saved
                (while unsaved it renders at the TOP of this Box instead). */}
            {!identityFirst && identityEditEl}

            {msg && (
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--ink)',
                  borderLeft: '3px solid var(--accent)',
                  paddingLeft: 10,
                }}
              >
                {msg}
              </p>
            )}
          </div>
        </Box>
      </div>

      {/* ═════ ASSIST SURFACE — chat assists, NEVER the boss (Option A) ═════
          The chat panel only PROPOSES structured changes; applying a proposal
          maps onto the RECORD on the left (via applyChatPatch → onChange).
          Nothing AI-inferred is persisted or sent to a client without a human
          tap. WhatsApp outbound stays OFF (inbound/simulate only). */}
      <div className="lead-assist">
        <div className="assist-flag">
          Chat — assists, never the boss · proposes into the record
        </div>
        <ChatSurface
          title={`Assist · ${lead.name} (${lead.id})`}
          note="Proposals land in the record only after you confirm"
          authorName={authorName}
          isTeamView={isTeamView}
          onApply={applyChatPatch}
          surface="lead"
        />
      </div>

      {/* The inline Record-a-payment modal — scoped to THIS lead's converted
          project so money is recorded on the same record (no jump). */}
      {payOpen && convertedProject && (
        <RecordPaymentFlow
          projects={liveProjects}
          initialProjectId={convertedProject.pid}
          onClose={() => setPayOpen(false)}
        />
      )}

      {/* M5-gaps ITEM 2 — post-conversion intake OFFER. Shown only after a
          successful convert. [Send Intake Form] opens IntakeLinkSendBar (its
          own service selector, L1A default + smart WA/email channel); [Not Now]
          closes + lands the staffer on the new project's detail page (the
          canonical /project/:pid route — replaces the old /conversation nav). */}
      {postConvert && !intakeOpen && (
        <div style={postCvOvl} role="dialog" aria-modal="true" aria-label="Project created">
          <div style={postCvSheet}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)', marginBottom: 6 }}>
              Project created successfully
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.6, marginBottom: 18 }}>
              Send intake form to <b style={{ color: 'var(--ink)' }}>{postConvert.clientName}</b> now?
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn3
                intent="ghost"
                size="sm"
                onClick={() => {
                  const pid = postConvert.pid;
                  setPostConvert(null);
                  navigate(`/project/${pid}`);
                }}
              >
                Not Now
              </Btn3>
              <Btn3 intent="grn" size="sm" onClick={() => setIntakeOpen(true)}>
                Send Intake Form
              </Btn3>
            </div>
          </div>
        </div>
      )}

      {postConvert && intakeOpen && (
        <IntakeLinkSendBar
          cid={postConvert.cid}
          pid={postConvert.pid}
          onClose={() => {
            // Close the SendBar AND clear the offer; then land on the project.
            const pid = postConvert.pid;
            setIntakeOpen(false);
            setPostConvert(null);
            navigate(`/project/${pid}`);
          }}
        />
      )}
    </div>
  );
}

/* Post-conversion intake-offer modal scaffold (tokens only). */
const postCvOvl: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(20,20,30,.42)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '12vh 16px',
  zIndex: 200,
  overflowY: 'auto',
};
const postCvSheet: React.CSSProperties = {
  background: '#fff',
  borderRadius: 'var(--r-lg)',
  width: 'min(420px, 100%)',
  boxShadow: '0 24px 60px rgba(20,20,30,.4)',
  padding: 22,
};

/* ════════════════════ MODULE 1 — the 4-step flow status band ════════════════════
 *
 * The one continuous flow on a single record (Option A): Lead in (incl.
 * WhatsApp) ▸ AI-drafted quote / agreed price ▸ Payment ▸ Convert to Project.
 * This band reads the record + the converted project and shows where the lead
 * is, plus the 6 must-haves (the Convert gate) — so the staffer sees the whole
 * journey at a glance and never has to leave the screen.
 */
function FlowStatusBand({
  lead,
  convertedProject,
  clientMatch,
}: {
  lead: Lead;
  convertedProject: LiveProject | null;
  clientMatch: ClientMatch | null;
}) {
  const isConverted = lead.state === 'converted';
  const hasAgreed =
    (typeof lead.agreedValue === 'number' && lead.agreedValue > 0) ||
    lead.noCharge === true;
  const paid =
    convertedProject != null &&
    (Object.values(convertedProject.moneyPaid ?? {}).some(Boolean) ||
      (convertedProject.clearedGateIds ?? []).length > 0);

  const steps: { label: string; done: boolean; now: boolean }[] = [
    { label: 'Lead in', done: true, now: !hasAgreed && !isConverted },
    {
      label: 'Quote / price',
      done: hasAgreed,
      now: hasAgreed && !isConverted,
    },
    { label: 'Payment', done: paid, now: isConverted && !paid },
    { label: 'Convert', done: isConverted, now: false },
  ];

  const checklist = leadChecklist(lead);
  const allOk = isLeadComplete(lead);

  return (
    <Box
      title="This engagement — one flow, one screen"
      note="Lead in (incl. WhatsApp) ▸ AI-drafted quote ▸ Payment ▸ Convert · no manual jumps"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* recognised-contact line */}
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          {clientMatch ? (
            <span>
              Recognised as existing client{' '}
              <Tag kind="val">
                {clientMatch.clientNo} · {clientMatch.name}
              </Tag>{' '}
              <span style={{ color: 'var(--ink-mute)' }}>
                (matched on {clientMatch.via} — no duplicate)
              </span>
            </span>
          ) : (
            <span style={{ color: 'var(--ink-mute)' }}>
              New contact — no existing client match on phone or email yet.
            </span>
          )}
        </div>

        {/* the 4-step rail */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {steps.map((s, i) => (
            <span
              key={s.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12.5,
                fontWeight: 700,
                padding: '5px 11px',
                borderRadius: 'var(--r-pill)',
                background: s.done
                  ? 'var(--good-wash)'
                  : s.now
                    ? 'var(--accent-wash)'
                    : 'var(--inset)',
                color: s.done
                  ? 'var(--good)'
                  : s.now
                    ? 'var(--accent-deep)'
                    : 'var(--ink-mute)',
                border: `1px solid ${s.now ? 'var(--accent)' : 'var(--rule)'}`,
              }}
            >
              <span>{s.done ? '✓' : i + 1}</span>
              {s.label}
            </span>
          ))}
        </div>

        {/* the 6 must-haves — the Convert gate, surfaced live */}
        {!isConverted && (
          <div>
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: allOk ? 'var(--good)' : 'var(--ink-soft)',
                marginBottom: 6,
              }}
            >
              {allOk
                ? 'All must-haves complete — ready to convert.'
                : 'Before this lead can convert (the 6 must-haves):'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {checklist.map((c) => (
                <span
                  key={c.key}
                  title={c.ok ? c.label : c.hint}
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    padding: '3px 9px',
                    borderRadius: 'var(--r-pill)',
                    background: c.ok ? 'var(--good-wash)' : 'var(--warn-wash)',
                    color: c.ok ? 'var(--good)' : 'var(--warn)',
                  }}
                >
                  {c.ok ? '✓' : '•'} {c.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
}

/* ════════════════════ IA FIX Q7 — existing-client picker ════════════════════
 *
 * Convert a lead to an EXISTING client — matched by client NUMBER and NAME
 * (spec Q7: both). Filters the live client directory by the pure
 * `matchesClientRow` predicate (number / name substring). The picked
 * client's clientId+clientNo feed `convertLeadPersist`'s `existing` target.
 */
function ExistingClientPicker({
  knownClients,
  search,
  onSearch,
  pickedClientId,
  onPick,
}: {
  knownClients: ClientRow[];
  search: string;
  onSearch: (s: string) => void;
  pickedClientId: string | null;
  onPick: (id: string | null) => void;
}) {
  const matches = knownClients.filter((c) => matchesClientRow(c, search));
  const shown = matches.slice(0, 12);

  return (
    <Card flat>
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search existing client by name or number…"
        aria-label="Search existing clients"
        style={inputStyle}
      />
      {knownClients.length === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--ink-mute)',
            margin: '8px 0 0',
          }}
        >
          No existing clients found. Switch to “New client”.
        </p>
      ) : matches.length === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--ink-mute)',
            margin: '8px 0 0',
          }}
        >
          No client matches “{search}”.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginTop: 8,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {shown.map((c) => {
            const on = c.clientId === pickedClientId;
            return (
              <button
                key={c.clientId}
                type="button"
                onClick={() => onPick(on ? null : c.clientId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  padding: '7px 10px',
                  borderRadius: 'var(--r-sm)',
                  border: `1px solid ${
                    on ? 'var(--accent)' : 'var(--rule)'
                  }`,
                  background: on ? 'var(--accent-wash)' : 'var(--surface)',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: 'var(--ink)',
                  fontFamily: 'inherit',
                }}
              >
                <b>{c.name}</b>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                  {c.clientNo}
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11.5,
                    color: 'var(--ink-mute)',
                  }}
                >
                  {c.projectCount} project{c.projectCount === 1 ? '' : 's'}
                </span>
              </button>
            );
          })}
          {matches.length > shown.length && (
            <span
              style={{
                fontSize: 11.5,
                color: 'var(--ink-mute)',
                padding: '2px 4px',
              }}
            >
              {matches.length - shown.length} more — refine the search.
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

/* ── The 3 entry-path inbound drafts ── */

function SIM_WEBSITE(): LeadInboundDraft {
  return {
    source: 'website',
    name: 'Website Enquiry (sim)',
    phone: '+91 98000 12345',
    email: 'web.enquiry@example.com',
    location: 'Pune',
    concern: 'Submitted the website Vastu enquiry form.',
    propertyType: 'residence',
    desiredTier: 'L1B',
    ownerName: 'Unassigned',
    notes: 'Simulated website-form inbound (stands in for real CF ingest).',
  };
}

function SIM_FREE_TOOL(): LeadInboundDraft {
  return {
    source: 'free_tool',
    name: 'Free-tool Result (sim)',
    phone: '+91 99000 54321',
    email: 'tool.user@example.com',
    location: 'Mumbai',
    concern: 'Completed a free Vastu tool — wants a deeper look.',
    propertyType: 'residence',
    desiredTier: 'L1A',
    ownerName: 'Unassigned',
    notes: 'Simulated free-tool inbound (stands in for real CF ingest).',
  };
}

/**
 * MODULE 1 — "Lead arrives via WhatsApp". Stands in for the real inbound
 * `avbp_wa_router.js` write (source 'whatsapp'). The owner edits the phone in
 * the record header (admin) to an EXISTING client's number — e.g. Mr. Sumit
 * C00407 — to see the inbox + record recognise the existing contact (no
 * duplicate). WhatsApp OUTBOUND is OFF; this only simulates the inbound.
 */
function SIM_WHATSAPP(): LeadInboundDraft {
  return {
    source: 'whatsapp',
    name: 'WhatsApp enquiry (sim)',
    phone: '+91 98200 00407',
    email: 'whatsapp.lead@example.com',
    location: '',
    concern: 'I want Vastu for my new shop.',
    propertyType: 'commercial',
    desiredTier: 'L1B',
    ownerName: 'Unassigned',
    notes:
      'Simulated WhatsApp inbound (stands in for the avbp_wa_router CF write). ' +
      'Edit the phone to an existing client number to see the existing-client match.',
  };
}
